/**
 * StateContextChip (v2 — 2026-02-28)
 * ─────────────────────────────────────────────────────────────
 * Header chip signalling BuildPilot's current live state (SA).
 * White background, black text. Opens a dropdown listing the
 * other 7 states with outlined SVG state icons and a "Notify me"
 * button that opens a small modal (email capture, submits to
 * /api/waitlist/state which fires an email to home@buildpilot.com.au).
 *
 * Design brief (user, 2026-02-28):
 *   - White chip with black writing (match Sign in / Sign up style
 *     surface but with different colour role)
 *   - No emoji, use SVG state outline instead
 *   - Drop "Coming soon" / "Join the waitlist" labels
 *   - "Notify me" opens a popup modal, not a new page
 *   - Popup submits → HQ inbox receives an email
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, X, CheckCircle2, Undo2 } from 'lucide-react';
import AustraliaStateIcon from './AustraliaStateIcon';
import {
  useUserState,
  setUserState,
  STATE_NAMES,
} from '../../lib/userState';

const OTHER_STATES = [
  { code: 'SA', name: 'South Australia' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'ACT', name: 'ACT' },
];

/**
 * States with a live content experience. Picking one navigates the
 * user into that state's hub instead of popping the "Notify me"
 * waitlist modal. Keep this list in sync with actual routes in App.js.
 */
const LIVE_STATE_ROUTES = {
  SA: '/',
  VIC: '/vic',
};

const isLiveState = (code) => Object.prototype.hasOwnProperty.call(LIVE_STATE_ROUTES, code);

const NotifyModal = ({ state, stateName, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const canSubmit = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && status === 'idle';

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist/state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, first_name: firstName, state, source: 'header-state-chip',
        }),
      });
      if (!res.ok) throw new Error();
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Notify me when BuildPilot launches in ${stateName}`}
      onClick={onClose}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="state-notify-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[420px] bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-[#86868b] hover:bg-black/5"
          aria-label="Close"
          data-testid="state-notify-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-6 sm:p-7">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#f5f5f7] text-[#1d1d1f] flex items-center justify-center">
              <AustraliaStateIcon state={state} className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide font-semibold text-[#86868b]">Waitlist</p>
              <p className="text-[15px] font-semibold text-[#1d1d1f]" data-testid="state-notify-modal-title">
                Notify me for {stateName}
              </p>
            </div>
          </div>

          {status === 'done' ? (
            <div className="text-center py-4" data-testid="state-notify-modal-done">
              <CheckCircle2 className="w-10 h-10 text-[#1d8efe] mx-auto mb-2" />
              <h3 className="text-base font-semibold text-[#1d1d1f] mb-1">You're on the list.</h3>
              <p className="text-[13px] text-[#424245] leading-relaxed">
                We'll email <strong>{email}</strong> the day BuildPilot launches in {stateName}. No spam in between.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 inline-flex items-center justify-center bg-[#1d1d1f] hover:bg-black text-white text-[13px] font-semibold px-5 py-2 rounded-full"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-[13px] text-[#424245] leading-relaxed mb-1">
                BuildPilot is currently live in South Australia and Victoria. Drop your details and you'll be the first to hear when {stateName} opens.
              </p>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name (optional)"
                className="w-full px-4 py-2.5 rounded-xl border border-[#e8e8ed] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1d1d1f] focus:border-transparent"
                data-testid="state-notify-modal-first-name"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 rounded-xl border border-[#e8e8ed] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1d1d1f] focus:border-transparent"
                data-testid="state-notify-modal-email"
              />
              {status === 'error' && (
                <p className="text-[12px] text-[#c53030]">Something went wrong. Please try again.</p>
              )}
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full inline-flex items-center justify-center bg-[#1d1d1f] hover:bg-black disabled:bg-[#a4a4ae] text-white text-[14px] font-semibold px-5 py-2.5 rounded-full transition-colors"
                data-testid="state-notify-modal-submit"
              >
                {status === 'submitting' ? 'Adding you…' : `Notify me for ${state}`}
              </button>
              <p className="text-[11px] text-[#86868b] text-center">
                Only one email — the launch. Never anything else.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const StateContextChip = ({ compact = false, inMenu = false, compactInMenu = false }) => {
  const [open, setOpen] = useState(false);
  const [notifyState, setNotifyState] = useState(null);
  const currentState = useUserState();
  const rootRef = useRef(null);
  const navigate = useNavigate();

  // Wrapper used by every state-select action in the dropdown. Persists
  // the user's declared state to localStorage, then routes:
  //  - SA or VIC: navigate to the state home if not already there
  //  - Every other state: navigate to /waitlist?state=X (Notify Me)
  // The old modal path is retired; the waitlist page is the single
  // capture surface for non-live states.
  const pickState = (stateObj) => {
    const code = stateObj?.code || stateObj;
    if (!code) return;
    setOpen(false);
    setNotifyState(null);
    setUserState(code);
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (isLiveState(code)) {
      // Only force-navigate when leaving a state-locked path (SA pick
      // while on /vic/*). Home and other universal surfaces stay put
      // so their state-aware content can adapt in place.
      if (code === 'SA' && path.startsWith('/vic')) navigate('/');
    } else {
      navigate(`/waitlist?state=${code}`);
    }
  };

  // Global "open the state-notify modal" listener - retained for
  // backward compatibility with any surface that still dispatches
  // `buildpilot:open-state-notify`. Now routes to /waitlist for
  // non-live states rather than showing a modal.
  useEffect(() => {
    const handle = (e) => {
      const code = (e && e.detail && e.detail.state) || currentState;
      if (!code || code === 'SA') return;
      if (isLiveState(code)) return;
      navigate(`/waitlist?state=${code}`);
    };
    window.addEventListener('buildpilot:open-state-notify', handle);
    return () => window.removeEventListener('buildpilot:open-state-notify', handle);
  }, [currentState, navigate]);
  // Refs to the pill button and the popup box so we can grab their
  // running CSS animation objects via the Web Animations API and copy
  // the pill's playback head into the popup - guaranteeing the two
  // shimmer gradients render at the exact same phase.
  const pillRef = useRef(null);
  const popupRef = useRef(null);

  // Fires BEFORE paint every time the popup mounts so it renders in-sync
  // on frame 1 (no flash). Rather than mirroring `currentTime` (which
  // drifts because each Animation ticks independently and starts a few
  // ms after the previous), we copy the pill's `startTime` onto the
  // popup - this anchors both animations to the same document-timeline
  // reference point, so they stay perfectly locked forever.
  useLayoutEffect(() => {
    if (!open) return;
    const pill = pillRef.current;
    const popup = popupRef.current;
    if (!pill || !popup) return;
    try {
      const pickGradient = (el) => {
        const anims = el.getAnimations ? el.getAnimations() : [];
        return anims.find((a) => a.animationName === 'gradientFlow') || anims[0] || null;
      };
      const pillAnim = pickGradient(pill);
      const popupAnim = pickGradient(popup);
      if (pillAnim && popupAnim) {
        // startTime may be null while an animation is pending - fall
        // back to currentTime mirroring in that case.
        if (pillAnim.startTime != null) {
          popupAnim.startTime = pillAnim.startTime;
        } else if (pillAnim.currentTime != null) {
          popupAnim.currentTime = pillAnim.currentTime;
        }
      }
    } catch {
      /* Web Animations API unavailable - both surfaces still animate,
         just may be phase-shifted on very old browsers. Acceptable. */
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // In-menu variant renders inside the hamburger drawer on mobile as
  // a collapsed button that expands to show the state list on tap -
  // same interaction as the desktop chip, sized to fit the drawer.
  // 2026-03-02: Swapped the white surface for the animated dark-green
  // gradient (`bp-state-green-bg`, 4s shimmer) so the chip pulses in
  // unison with the header's Download-App (purple) and Sign-in (blue)
  // gradient CTAs. Text + icons flip to white for contrast.
  if (inMenu) {
    const inMenuLabel = currentState === 'SA' ? 'South Australia' : (STATE_NAMES[currentState] || currentState);
    if (compactInMenu) {
      return (
        <>
          <button
            ref={pillRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="state-context-in-menu-list"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/[0.07] border border-white/15 text-white hover:bg-white/[0.12] transition-colors"
            data-testid="state-context-chip-in-menu-toggle"
          >
            <AustraliaStateIcon state={currentState} className="w-4 h-4 text-[#34d17f]" />
            <span className="text-[12px] text-white/60 font-medium">Viewing</span>
            <span className="text-[13px] font-semibold text-white leading-none">{inMenuLabel}</span>
            <ChevronDown className={`w-3.5 h-3.5 text-white/70 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div
              ref={popupRef}
              id="state-context-in-menu-list"
              className="mt-2 rounded-2xl overflow-hidden bp-state-green-bg shadow-[0_4px_14px_rgba(13,61,30,0.35)]"
              data-testid="state-context-chip-in-menu-list"
            >
              <p className="px-4 pt-3 pb-2 text-[11px] text-white/80 leading-snug border-b border-white/15">
                {currentState === 'VIC'
                  ? "You're on the Victoria edition. Prices, rules and builders apply to VIC only."
                  : currentState === 'SA'
                    ? 'Prices, rules and builders apply to SA only. Not in SA? Pick your state below.'
                    : `You picked ${STATE_NAMES[currentState] || currentState}. We're not live there yet - join the waitlist or view a live state below.`}
              </p>
              {currentState !== 'SA' && (
                <button
                  type="button"
                  onClick={() => pickState('SA')}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors text-left border-b border-white/15"
                  data-testid="state-context-chip-in-menu-reset-sa"
                >
                  <span className="flex items-center gap-3">
                    <Undo2 className="w-5 h-5 text-white" strokeWidth={2} />
                    <span className="text-[14px] text-white">Switch back to South Australia</span>
                  </span>
                </button>
              )}
              {OTHER_STATES.map((s) => {
                const selected = currentState === s.code;
                return (
                  <button
                    key={s.code}
                    type="button"
                    onClick={() => pickState(s)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors text-left border-b border-white/10 last:border-b-0 ${selected ? 'bg-white/[0.08]' : ''}`}
                    data-testid={`state-context-chip-in-menu-${s.code.toLowerCase()}`}
                  >
                    <span className="flex items-center gap-3">
                      <AustraliaStateIcon state={s.code} className="w-5 h-5 text-white" />
                      <span className="text-[14px] text-white">{s.name}</span>
                    </span>
                    {selected && (
                      <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={2.4} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      );
    }
    return (
      <>
        <button
          ref={pillRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="state-context-in-menu-list"
          className="w-full flex items-center justify-between gap-3 px-5 py-3 rounded-full bp-state-green-bg text-white hover:opacity-95 transition-opacity shadow-[0_4px_14px_rgba(0,165,80,0.45)]"
          data-testid="state-context-chip-in-menu-toggle"
        >
          <span className="flex items-center gap-3">
            <AustraliaStateIcon state={currentState} className="w-5 h-5 text-white" />
            <span className="flex flex-col text-left">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-white/75 leading-none mb-0.5">
                {isLiveState(currentState) ? 'Currently viewing' : 'You picked'}
              </span>
              <span className="text-[14px] font-semibold text-white leading-tight">
                {inMenuLabel}
              </span>
            </span>
          </span>
          <ChevronDown className={`w-4 h-4 text-white transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div
            ref={popupRef}
            id="state-context-in-menu-list"
            className="mt-2 rounded-2xl overflow-hidden bp-state-green-bg shadow-[0_4px_14px_rgba(13,61,30,0.35)]"
            data-testid="state-context-chip-in-menu-list"
          >
            <p className="px-4 pt-3 pb-2 text-[11px] text-white/80 leading-snug border-b border-white/15">
              {currentState === 'VIC'
                ? "You're on the Victoria edition. Prices, rules and builders apply to VIC only."
                : currentState === 'SA'
                  ? 'Prices, rules and builders apply to SA only. Not in SA? Pick your state below.'
                  : `You picked ${STATE_NAMES[currentState] || currentState}. We're not live there yet - join the waitlist or view a live state below.`}
            </p>
            {currentState !== 'SA' && (
              <button
                type="button"
                onClick={() => pickState('SA')}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors text-left border-b border-white/15"
                data-testid="state-context-chip-in-menu-reset-sa"
              >
                <span className="flex items-center gap-3">
                  <Undo2 className="w-5 h-5 text-white" strokeWidth={2} />
                  <span className="text-[14px] text-white">Switch back to South Australia</span>
                </span>
              </button>
            )}
            {OTHER_STATES.map((s) => {
              const selected = currentState === s.code;
              return (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => pickState(s)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors text-left border-b border-white/10 last:border-b-0 ${selected ? 'bg-white/[0.08]' : ''}`}
                  data-testid={`state-context-chip-in-menu-${s.code.toLowerCase()}`}
                >
                  <span className="flex items-center gap-3">
                    <AustraliaStateIcon state={s.code} className="w-5 h-5 text-white" />
                    <span className="text-[14px] text-white">{s.name}</span>
                  </span>
                  {selected && (
                    <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={2.4} />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {notifyState && (
          <NotifyModal
            state={notifyState.code}
            stateName={notifyState.name}
            onClose={() => setNotifyState(null)}
          />
        )}
      </>
    );
  }

  const sizeCls = compact
    ? 'h-9 px-3 text-[12px] gap-1.5'
    : 'h-9 xl:h-10 px-3.5 xl:px-4 text-[13px] xl:text-sm gap-1.5';

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={pillRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`BuildPilot state - ${STATE_NAMES[currentState] || currentState}`}
        className={`inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap transition-opacity bp-state-green-bg text-white hover:opacity-95 shadow-[0_4px_14px_rgba(0,165,80,0.4)] ${sizeCls}`}
        data-testid="state-context-chip"
      >
        <AustraliaStateIcon state={currentState} className="w-4 h-4 text-white" />
        <span>{currentState}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={popupRef}
          role="listbox"
          className="absolute right-0 mt-2 w-[280px] rounded-2xl shadow-2xl overflow-hidden z-[100] bp-state-green-bg"
          data-testid="state-context-chip-menu"
        >
          <div className="px-4 py-3 border-b border-white/15">
            <div className="flex items-center gap-2.5">
              <AustraliaStateIcon state={currentState} className="w-5 h-5 text-white" />
              <div>
                <p className="text-[13px] font-semibold text-white">
                  {STATE_NAMES[currentState] || currentState}
                </p>
                <p className="text-[11px] text-white/75 leading-tight">
                  {currentState === 'VIC'
                    ? "You're on the Victoria edition."
                    : currentState === 'SA'
                      ? 'Prices, rules and builders apply to SA only.'
                      : "We're not live in your state yet - join the waitlist or view a live state."}
                </p>
              </div>
            </div>
          </div>
          {/* Simple state selector - every state clickable. SA + VIC
              switch content in place; every other state routes to the
              waitlist capture page. */}
          <div className="py-1">
            {OTHER_STATES.map((s) => {
              const selected = currentState === s.code;
              return (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => { setOpen(false); pickState(s); }}
                  className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-[13px] hover:bg-white/10 transition-colors text-left ${selected ? 'bg-white/[0.08]' : ''}`}
                  data-testid={`state-context-chip-notify-${s.code.toLowerCase()}`}
                >
                  <span className="flex items-center gap-2.5">
                    <AustraliaStateIcon state={s.code} className="w-4 h-4 text-white" />
                    <span className="text-white">{s.name}</span>
                  </span>
                  {selected && (
                    <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2.4} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {notifyState && (
        <NotifyModal
          state={notifyState.code}
          stateName={notifyState.name}
          onClose={() => setNotifyState(null)}
        />
      )}
    </div>
  );
};

export default StateContextChip;
